<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

// audienceConfig mirrors the backend's Campaigns::AudienceResolutionService
// contract exactly (mode + mode-specific keys) - see that service for the
// authoritative shape. Never resolved here, only assembled and sent as-is.
const audienceConfig = defineModel('audienceConfig', {
  type: Object,
  required: true,
});
// Lifted to the wizard (rather than kept as local state read back via a
// template ref) because this step is v-if-unmounted once the wizard moves
// past it - a template ref into an unmounted component would go stale, and
// the Review step still needs to display the last known estimate.
const estimatedCount = defineModel('estimatedCount', {
  type: Number,
  default: null,
});
const isConfigComplete = defineModel('isConfigComplete', {
  type: Boolean,
  default: false,
});

const { t } = useI18n();
const store = useStore();

const labels = useMapGetter('labels/getLabels');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');

const isEstimating = ref(false);
const estimateError = ref('');

const AUDIENCE_MODES = [
  {
    id: 'all_contacts',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.ALL_CONTACTS.LABEL'),
    description: t(
      'CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.ALL_CONTACTS.DESCRIPTION'
    ),
  },
  {
    id: 'labels',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.LABELS.LABEL'),
    description: t(
      'CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.LABELS.DESCRIPTION'
    ),
  },
  {
    id: 'labels_include_exclude',
    label: t(
      'CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.LABELS_INCLUDE_EXCLUDE.LABEL'
    ),
    description: t(
      'CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.LABELS_INCLUDE_EXCLUDE.DESCRIPTION'
    ),
  },
  {
    id: 'custom_filter',
    label: t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.CUSTOM_FILTER.LABEL'),
    description: t(
      'CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.MODES.CUSTOM_FILTER.DESCRIPTION'
    ),
  },
];

const labelOptions = computed(() =>
  (labels.value || []).map(label => ({
    value: label.title,
    label: label.title,
  }))
);

const customFilterOptions = computed(() =>
  (contactCustomViews.value || []).map(view => ({
    value: view.id,
    label: view.name,
  }))
);

const selectMode = mode => {
  audienceConfig.value = { mode };
};

const selectedLabelTitles = computed({
  get: () => audienceConfig.value.label_titles || [],
  set: value => {
    audienceConfig.value = { ...audienceConfig.value, label_titles: value };
  },
});

const includeLabelTitles = computed({
  get: () => audienceConfig.value.include_label_titles || [],
  set: value => {
    audienceConfig.value = {
      ...audienceConfig.value,
      include_label_titles: value,
    };
  },
});

const excludeLabelTitles = computed({
  get: () => audienceConfig.value.exclude_label_titles || [],
  set: value => {
    audienceConfig.value = {
      ...audienceConfig.value,
      exclude_label_titles: value,
    };
  },
});

const customFilterId = computed({
  get: () => audienceConfig.value.custom_filter_id || null,
  set: value => {
    audienceConfig.value = { ...audienceConfig.value, custom_filter_id: value };
  },
});

// Only used to decide when it's meaningful to call the estimate endpoint (and
// to gate the wizard's Next button) - mirrors (loosely) the required-field
// checks the backend's Campaigns::AudienceResolutionService itself enforces,
// so we don't fire a request that's guaranteed to 422.
const checkConfigComplete = () => {
  switch (audienceConfig.value.mode) {
    case 'all_contacts':
      return true;
    case 'labels':
      return (audienceConfig.value.label_titles || []).length > 0;
    case 'labels_include_exclude':
      return (audienceConfig.value.include_label_titles || []).length > 0;
    case 'custom_filter':
      return !!audienceConfig.value.custom_filter_id;
    default:
      return false;
  }
};

let debounceTimeout = null;
const fetchEstimate = async () => {
  if (!isConfigComplete.value) {
    estimatedCount.value = null;
    return;
  }
  isEstimating.value = true;
  estimateError.value = '';
  try {
    const count = await store.dispatch(
      'campaigns/estimateAudience',
      audienceConfig.value
    );
    estimatedCount.value = count;
  } catch (error) {
    estimateError.value = t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.ESTIMATE_ERROR');
    estimatedCount.value = null;
  } finally {
    isEstimating.value = false;
  }
};

watch(
  audienceConfig,
  () => {
    // Completeness (and therefore the wizard's Next button) updates
    // immediately; only the actual network call is debounced.
    isConfigComplete.value = checkConfigComplete();
    clearTimeout(debounceTimeout);
    debounceTimeout = setTimeout(fetchEstimate, 400);
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('customViews/get', 'contact');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <p class="text-sm text-n-slate-11">
      {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.STEP_LABEL') }}
    </p>

    <div class="flex flex-col gap-3">
      <RadioCard
        v-for="mode in AUDIENCE_MODES"
        :id="mode.id"
        :key="mode.id"
        :label="mode.label"
        :description="mode.description"
        :is-active="audienceConfig.mode === mode.id"
        @select="selectMode"
      />
    </div>

    <div v-if="audienceConfig.mode === 'labels'" class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="selectedLabelTitles"
        :options="labelOptions"
        :placeholder="t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_PLACEHOLDER')"
      />
    </div>

    <div
      v-else-if="audienceConfig.mode === 'labels_include_exclude'"
      class="flex flex-col gap-3"
    >
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.INCLUDE_LABELS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="includeLabelTitles"
          :options="labelOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_PLACEHOLDER')
          "
        />
      </div>
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.EXCLUDE_LABELS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          v-model="excludeLabelTitles"
          :options="labelOptions"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.LABELS_PLACEHOLDER')
          "
        />
      </div>
    </div>

    <div
      v-else-if="audienceConfig.mode === 'custom_filter'"
      class="flex flex-col gap-1"
    >
      <label class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.CUSTOM_FILTER_LABEL') }}
      </label>
      <ComboBox
        v-model="customFilterId"
        :options="customFilterOptions"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.CUSTOM_FILTER_PLACEHOLDER')
        "
      />
      <p
        v-if="!customFilterOptions.length"
        class="mt-1 text-xs text-n-slate-11"
      >
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.NO_SAVED_SEGMENTS') }}
      </p>
    </div>

    <div
      class="flex items-center gap-2 p-3 text-sm rounded-lg bg-n-alpha-black2"
    >
      <Spinner v-if="isEstimating" :size="16" />
      <template v-else-if="estimateError">
        <span class="text-n-ruby-11">{{ estimateError }}</span>
      </template>
      <template v-else-if="estimatedCount !== null">
        <span class="font-medium text-n-slate-12">{{ estimatedCount }}</span>
        <span class="text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.ESTIMATED_RECIPIENTS') }}
        </span>
      </template>
      <span v-else class="text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.WIZARD.AUDIENCE.CONFIGURE_TO_SEE_ESTIMATE') }}
      </span>
    </div>
  </div>
</template>
